import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useDuelRealtime } from '../../../hooks/useDuelRealtime';

const CompetitiveProgressBar = ({ duel: initialDuel, userId }) => {
  const [duel, setDuel] = useState(initialDuel);

  // Update local state when prop changes
  useEffect(() => {
    setDuel(initialDuel);
  }, [initialDuel]);

  // Subscribe to real-time progress updates
  useDuelRealtime(duel?.id, userId, {
    onDuelUpdate: (payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new) {
        // Update progress values in real-time
        setDuel(prev => ({
          ...prev,
          creator_progress: payload?.new?.creator_progress,
          opponent_progress: payload?.new?.opponent_progress,
          status: payload?.new?.status,
          winner_id: payload?.new?.winner_id
        }));
      }
    },
    onProofUpload: (payload) => {
      // Trigger animation when proof is uploaded
      if (payload?.new) {
        setDuel(prev => ({ ...prev })); // Force re-render for animation
      }
    }
  });

  const isCreator = duel?.creator_id === userId;
  const userProfile = isCreator ? duel?.creator : duel?.opponent;
  const opponentProfile = isCreator ? duel?.opponent : duel?.creator;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
  
  const userPercentage = Math.min((userProgress / duel?.target_value) * 100, 100);
  const opponentPercentage = Math.min((opponentProgress / duel?.target_value) * 100, 100);
  
  const isWinning = userProgress > opponentProgress;
  const isLosing = userProgress < opponentProgress;
  const isTied = userProgress === opponentProgress;

  const getStatusText = () => {
    if (isTied) return '¡Empatados!';
    if (isWinning) return '¡Vas Ganando!';
    return 'Vas Perdiendo';
  };

  const getStatusColor = () => {
    if (isTied) return 'text-accent';
    if (isWinning) return 'text-success';
    return 'text-error';
  };

  const getStatusIcon = () => {
    if (isTied) return 'Equal';
    if (isWinning) return 'TrendingUp';
    return 'TrendingDown';
  };

  return (
    <div className="space-y-4">
      {/* Status Indicator */}
      <div className="flex items-center justify-center space-x-2">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
            isWinning ? 'bg-success/20 border border-success/30' : isLosing ?'bg-error/20 border border-error/30': 'bg-accent/20 border border-accent/30'
          }`}
        >
          <Icon name={getStatusIcon()} size={20} className={getStatusColor()} />
          <span className={`font-bold ${getStatusColor()}`}>{getStatusText()}</span>
        </motion.div>
      </div>
      {/* VS Layout */}
      <div className="relative">
        {/* User Side */}
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div className="text-left">
            <div className="flex items-center space-x-2 mb-2">
              {userProfile?.avatar_url ? (
                <img src={userProfile?.avatar_url} alt={userProfile?.full_name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {userProfile?.full_name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-foreground">Tú</p>
                <p className="text-xs text-muted-foreground">{userProfile?.full_name}</p>
              </div>
            </div>
            <div className="text-right">
              <motion.p 
                className="text-3xl font-bold text-primary"
                key={userProgress}
                initial={{ scale: 1.5, color: '#10b981' }}
                animate={{ scale: 1, color: 'currentColor' }}
                transition={{ duration: 0.3 }}
              >
                {userProgress}
              </motion.p>
              <p className="text-xs text-muted-foreground">de {duel?.target_value}</p>
            </div>
          </div>

          {/* VS Badge */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-error to-warning flex items-center justify-center shadow-lg border-4 border-card">
              <span className="text-white font-bold text-sm">VS</span>
            </div>
          </div>

          {/* Opponent Side */}
          <div className="text-right">
            <div className="flex items-center justify-end space-x-2 mb-2">
              <div className="text-right">
                <p className="text-sm font-semibold text-foreground">{opponentProfile?.full_name}</p>
                <p className="text-xs text-muted-foreground">Oponente</p>
              </div>
              {opponentProfile?.avatar_url ? (
                <img src={opponentProfile?.avatar_url} alt={opponentProfile?.full_name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-sm font-semibold text-muted-foreground">
                    {opponentProfile?.full_name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <div className="text-left">
              <motion.p 
                className="text-3xl font-bold text-muted-foreground"
                key={opponentProgress}
                initial={{ scale: 1.5, color: '#10b981' }}
                animate={{ scale: 1, color: 'currentColor' }}
                transition={{ duration: 0.3 }}
              >
                {opponentProgress}
              </motion.p>
              <p className="text-xs text-muted-foreground">de {duel?.target_value}</p>
            </div>
          </div>
        </div>

        {/* Comparative Progress Bars */}
        <div className="space-y-2 mt-4">
          {/* User Progress Bar */}
          <div className="relative">
            <div className="w-full bg-muted/50 rounded-full h-4 overflow-hidden">
              <motion.div
                className={`h-4 rounded-full ${
                  isWinning ? 'bg-gradient-to-r from-success to-success/80' : isLosing ?'bg-gradient-to-r from-error to-error/80': 'bg-gradient-to-r from-accent to-accent/80'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${userPercentage}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-white drop-shadow">{Math.round(userPercentage)}%</span>
            </div>
          </div>

          {/* Opponent Progress Bar */}
          <div className="relative">
            <div className="w-full bg-muted/50 rounded-full h-4 overflow-hidden">
              <motion.div
                className="h-4 rounded-full bg-gradient-to-r from-muted-foreground to-muted-foreground/80"
                initial={{ width: 0 }}
                animate={{ width: `${opponentPercentage}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-white drop-shadow">{Math.round(opponentPercentage)}%</span>
            </div>
          </div>
        </div>
      </div>
      {/* Competitive Copy */}
      <div className="text-center">
        <motion.p 
          className="text-sm font-medium text-muted-foreground"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          {isWinning && '¡Sigue así! Mantén la ventaja 💪'}
          {isLosing && '¡No te rindas! Aún puedes alcanzarlo 🔥'}
          {isTied && '¡Cabeza a cabeza! Cada punto cuenta ⚡'}
        </motion.p>
      </div>
    </div>
  );
};

export default CompetitiveProgressBar;